<template>
  <div class="relative" ref="rootEl">
    <!-- Bell icon button — sticky note (Phosphor-style: paper dengan folded corner) -->
    <button
      class="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-amber-600 hover:bg-gray-100 transition-colors"
      title="Personal Notes"
      @click="togglePanel"
    >
      <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
        <path d="M20 14V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9"/>
        <path d="M20 14h-4a2 2 0 0 0-2 2v4"/>
        <path d="M20 14l-6 6"/>
        <path d="M8 7h8"/>
        <path d="M8 11h6"/>
      </svg>
      <span
        v-if="openedCount > 0"
        class="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center border border-white"
      >{{ openedCount }}</span>
    </button>

    <!-- Dropdown list -->
    <Teleport to="body">
      <Transition enter-active-class="transition ease-out duration-150" enter-from-class="opacity-0 -translate-y-1" leave-active-class="transition ease-in duration-100" leave-to-class="opacity-0 -translate-y-1">
        <div v-if="panelOpen" :style="dropdownStyle" class="fixed w-80 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden z-[100]">
          <div class="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-b from-amber-50 to-white">
            <p class="text-sm font-bold text-gray-800">Personal Notes</p>
            <button
              class="text-[11px] font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
              @click="createNewNote"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
              New Notes
            </button>
          </div>
          <div class="max-h-96 overflow-y-auto">
            <p v-if="loading" class="px-4 py-6 text-xs text-gray-400 italic text-center">Memuat…</p>
            <p v-else-if="!notes.length" class="px-4 py-6 text-xs text-gray-400 italic text-center">Belum ada note. Klik <span class="font-semibold">New Notes</span> untuk mulai.</p>
            <div
              v-for="n in notes" :key="n.id"
              class="group/notes-row w-full text-left px-4 py-2.5 border-b border-gray-50 last:border-0 hover:bg-gray-50 flex items-start gap-2.5 cursor-pointer"
              @click="openNote(n)"
            >
              <span class="inline-block w-3 h-3 rounded-full flex-shrink-0 mt-0.5" :class="colorDot(n.color)" />
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold text-gray-800 truncate">{{ n.title || '(tanpa judul)' }}</p>
                <p class="text-[11px] text-gray-500 line-clamp-2 leading-snug">{{ n.content || '—' }}</p>
                <p class="text-[9px] text-gray-400 mt-0.5">{{ formatDt(n.updatedAt) }}<span v-if="n.opened" class="ml-1 text-emerald-600 font-semibold">· terbuka</span></p>
              </div>
              <button
                type="button"
                class="opacity-0 group-hover/notes-row:opacity-100 text-gray-300 hover:text-red-500 flex-shrink-0"
                title="Hapus"
                @click.stop="deleteNote(n.id)"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
                  <path d="M3 6h18"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <line x1="10" x2="10" y1="11" y2="17"/>
                  <line x1="14" x2="14" y1="11" y2="17"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Floating sticky notes (rendered at top of all overlays). z-[2147483646]
         = browser hard limit minus 1 supaya selalu di paling atas, kecuali
         system toast yang dipakai untuk konfirmasi. -->
    <Teleport to="body">
      <div
        v-for="n in openedNotes" :key="`sticky-${n.id}`"
        class="fixed rounded-lg flex flex-col ring-1 ring-black/5 transition-shadow"
        :class="[
          colorBg(n.color),
          dragState.noteId === n.id
            ? 'shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35),0_8px_16px_-8px_rgba(0,0,0,0.2)]'
            : 'shadow-[0_12px_30px_-12px_rgba(0,0,0,0.25),0_4px_10px_-4px_rgba(0,0,0,0.15)]',
        ]"
        :style="{
          left:   n.posX  + 'px',
          top:    n.posY  + 'px',
          width:  n.width + 'px',
          height: n.height + 'px',
          zIndex: 2147483646,
        }"
        @pointerdown="onStickyPointerDown($event, n)"
        @pointermove="onStickyPointerMove($event, n)"
        @pointerup="onStickyPointerUp($event)"
        @pointercancel="onStickyPointerUp($event)"
      >
        <!-- Header (drag handle) — translucent gradient, no heavy divider -->
        <div
          :data-sticky-drag="n.id"
          class="px-4 pt-3 pb-2.5 flex items-center gap-2 select-none rounded-t-lg"
          :class="[
            colorHeader(n.color),
            dragState.noteId === n.id && dragState.mode === 'drag' ? 'cursor-grabbing' : 'cursor-grab',
          ]"
        >
          <input
            v-model="n.title"
            type="text"
            placeholder="Judul…"
            :readonly="n.role === 'viewer'"
            class="flex-1 min-w-0 bg-transparent text-base font-bold text-gray-800 placeholder-gray-400/70 focus:outline-none truncate tracking-tight leading-tight"
            :data-sticky-no-drag="n.id"
            @input="schedulePatch(n)"
          />
          <div class="flex items-center gap-1" :data-sticky-no-drag="n.id">
            <span
              v-if="n.role && n.role !== 'owner'"
              class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide"
              :class="n.role === 'editor' ? 'bg-emerald-200/70 text-emerald-800' : 'bg-gray-200/70 text-gray-700'"
              :title="`Shared oleh ${n.ownerName} sebagai ${n.role}`"
            >{{ n.role }}</span>
            <button
              v-if="n.role === 'owner' || !n.role"
              class="w-5 h-5 inline-flex items-center justify-center text-gray-500/70 hover:text-gray-800 hover:bg-white/60 rounded transition-colors"
              title="Share dengan user lain"
              @click="openShareDialog(n)"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
            <button
              class="w-5 h-5 inline-flex items-center justify-center text-gray-500/70 hover:text-gray-800 hover:bg-white/60 rounded transition-colors"
              title="Tutup (note tetap tersimpan)"
              @click="closeNote(n)"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
        <!-- Content textarea — clean, no border, soft serif-ish via system stack -->
        <textarea
          v-model="n.content"
          placeholder="Tulis apa saja di sini…"
          :readonly="n.role === 'viewer'"
          :data-sticky-no-drag="n.id"
          class="flex-1 w-full bg-transparent px-3 pt-1 pb-10 text-[13px] text-gray-800 placeholder-gray-400/70 focus:outline-none resize-none leading-relaxed"
          @input="schedulePatch(n)"
        />
        <!-- Color picker — hover zone di pojok kiri bawah. Picker hanya tampil
             saat user hover area ini, supaya tidak distract saat menulis. -->
        <div
          v-if="n.role !== 'viewer'"
          :data-sticky-no-drag="n.id"
          class="group/colorpicker absolute bottom-0 left-0 w-36 h-12 flex items-end justify-start p-2"
        >
          <div
            class="flex items-center gap-0.5 px-1.5 py-1 rounded-full bg-white/60 backdrop-blur-sm shadow-sm opacity-0 group-hover/colorpicker:opacity-100 transition-opacity duration-150"
          >
            <button
              v-for="c in COLORS" :key="c.key"
              type="button"
              class="w-3 h-3 rounded-full hover:scale-125 transition-transform ring-1 ring-black/10"
              :class="[c.dot, n.color === c.key ? 'ring-2 ring-gray-800/70 scale-110' : '']"
              :title="`Warna ${c.key}`"
              @click="setColor(n, c.key)"
            />
          </div>
        </div>
        <!-- Folded-corner decoration di pojok kanan bawah (subtle, gives "paper" vibe) -->
        <div class="absolute bottom-0 right-0 w-5 h-5 pointer-events-none"
          :class="colorFoldedCorner(n.color)"
          style="clip-path: polygon(100% 0, 100% 100%, 0 100%);"
        />
        <!-- Resize handle pojok kanan bawah (di atas folded corner) -->
        <div
          :data-sticky-resize="n.id"
          class="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-10 flex items-end justify-end p-0.5"
          title="Drag untuk resize"
        >
          <svg class="w-2.5 h-2.5 text-gray-600/70" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
            <path d="M22 22L12 22"/>
            <path d="M22 22L22 12"/>
          </svg>
        </div>
      </div>
    </Teleport>

    <!-- Share Modal — owner manages note shares (add/remove user + role) -->
    <Teleport to="body">
      <div
        v-if="shareDialog.open"
        class="fixed inset-0 z-[2147483647] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        @click.self="closeShareDialog"
      >
        <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden max-h-[85vh]">
          <div class="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
            <div class="min-w-0">
              <h2 class="text-sm font-bold text-gray-900">Share Note</h2>
              <p class="text-[11px] text-gray-400 mt-0.5 truncate">{{ shareDialog.note?.title || '(tanpa judul)' }}</p>
            </div>
            <button class="w-7 h-7 rounded-lg text-gray-400 hover:bg-gray-100 flex items-center justify-center" @click="closeShareDialog">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="px-5 py-4 space-y-3 overflow-y-auto">
            <!-- Add new share -->
            <div>
              <label class="block text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">Cari User</label>
              <div class="relative">
                <input
                  v-model="shareDialog.searchInput"
                  type="text"
                  placeholder="Nama atau email..."
                  class="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-amber-300"
                  @focus="shareDialog.searchFocused = true"
                  @blur="onShareSearchBlur"
                />
                <div
                  v-if="shareDialog.searchFocused && shareDialog.searchResults.length"
                  class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto"
                >
                  <button
                    v-for="u in shareDialog.searchResults" :key="u.id"
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0 text-xs"
                    @mousedown.prevent="addShare(u, 'viewer')"
                  >
                    <p class="font-semibold text-gray-800">{{ u.name }}</p>
                    <p class="text-[10px] text-gray-400">{{ u.email }}</p>
                  </button>
                </div>
                <p v-if="shareDialog.searchFocused && shareDialog.searchInput && !shareDialog.searching && !shareDialog.searchResults.length" class="text-[11px] text-gray-400 italic mt-1">Tidak ada user cocok.</p>
              </div>
              <p class="text-[10px] text-gray-400 mt-1">Klik user untuk add sebagai <span class="font-semibold">Viewer</span> (default). Ubah role di list di bawah.</p>
            </div>

            <!-- Current shares list -->
            <div>
              <p class="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">Sudah Di-share Ke</p>
              <p v-if="shareDialog.loading" class="text-[11px] text-gray-400 italic">Memuat…</p>
              <p v-else-if="!shareDialog.shares.length" class="text-[11px] text-gray-400 italic">Belum di-share ke siapapun.</p>
              <div v-else class="border border-gray-100 rounded-lg divide-y divide-gray-50">
                <div
                  v-for="s in shareDialog.shares" :key="s.userId"
                  class="flex items-center gap-2 px-3 py-2"
                >
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-semibold text-gray-800 truncate">{{ s.name }}</p>
                    <p class="text-[10px] text-gray-400 truncate">{{ s.email }}</p>
                  </div>
                  <select
                    v-model="s.role"
                    class="text-[11px] border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-amber-300"
                    @change="updateShareRole(s)"
                  >
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button
                    type="button"
                    class="w-6 h-6 inline-flex items-center justify-center rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Hapus share"
                    @click="removeShare(s)"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
                      <path d="M3 6h18"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div class="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-[10px] text-amber-800 leading-snug">
              <p><span class="font-semibold">Editor</span>: bisa edit judul, isi & warna note.</p>
              <p><span class="font-semibold">Viewer</span>: hanya bisa lihat (read-only).</p>
            </div>
          </div>
          <div class="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
            <button class="px-3 py-1.5 text-xs rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold" @click="closeShareDialog">Tutup</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
interface Note {
  id: number
  userId: number
  title: string
  content: string
  color: string
  posX: number
  posY: number
  width: number
  height: number
  opened: boolean
  createdAt: string
  updatedAt: string
  // Enrichment dari GET /api/personal-notes — owner: dimiliki user, editor:
  // di-share dengan role editor, viewer: di-share dengan role viewer.
  role?: 'owner' | 'editor' | 'viewer'
  ownerName?: string | null
}
interface ShareRow {
  userId: number
  role: 'editor' | 'viewer'
  name: string
  email: string
}
interface UserSearchRow {
  id: number
  name: string
  email: string
}

const COLORS = [
  { key: 'yellow', dot: 'bg-yellow-400' },
  { key: 'pink',   dot: 'bg-pink-400'   },
  { key: 'blue',   dot: 'bg-sky-400'    },
  { key: 'green',  dot: 'bg-emerald-400'},
  { key: 'purple', dot: 'bg-violet-400' },
  { key: 'orange', dot: 'bg-orange-400' },
] as const

// Shared state — bell + sticky-renderer share the same notes list. Pakai
// useState supaya state hidup di seluruh halaman (tidak reset saat navigate).
const notes = useState<Note[]>('personal-notes', () => [])
const loading = ref(false)
const openedNotes = computed(() => notes.value.filter(n => n.opened))
const openedCount = computed(() => openedNotes.value.length)

// ── Bell dropdown ─────────────────────────────────────────────────────
const rootEl  = ref<HTMLElement | null>(null)
const panelOpen = ref(false)
const dropdownStyle = ref<Record<string, string>>({ top: '52px', right: '12px' })
function positionPanel() {
  if (!rootEl.value) return
  const r = rootEl.value.getBoundingClientRect()
  dropdownStyle.value = {
    top:   `${r.bottom + 6}px`,
    right: `${Math.max(8, window.innerWidth - r.right)}px`,
  }
}
async function togglePanel() {
  panelOpen.value = !panelOpen.value
  if (panelOpen.value) {
    positionPanel()
    await loadNotes()
  }
}
function onWindowClick(e: MouseEvent) {
  if (!panelOpen.value) return
  const t = e.target as HTMLElement | null
  if (!t) return
  if (rootEl.value && rootEl.value.contains(t)) return
  // Klik di dalam dropdown? Dropdown ada di body via teleport — cek apakah
  // klik di-handle oleh element dengan z-100 dropdown class. Kalau bukan
  // bagian dropdown, close.
  if (t.closest('[data-personal-notes-panel]')) return
  panelOpen.value = false
}
onMounted(() => {
  loadNotes()
  window.addEventListener('mousedown', onWindowClick)
})
onBeforeUnmount(() => {
  window.removeEventListener('mousedown', onWindowClick)
})

// ── Data fetching ─────────────────────────────────────────────────────
async function loadNotes() {
  loading.value = true
  try {
    const res = await $fetch<{ rows: Note[] }>('/api/personal-notes')
    notes.value = res.rows ?? []
  } catch {
    notes.value = []
  } finally {
    loading.value = false
  }
}

async function createNewNote() {
  // Spawn ke posisi acak (random offset) supaya tidak overlap exact dengan
  // note lain. Stagger ~24px per note yang sudah open.
  const offset = (openedNotes.value.length % 8) * 24
  try {
    const created = await $fetch<Note>('/api/personal-notes', {
      method: 'POST',
      body: {
        title:  '',
        content: '',
        color:  'yellow',
        posX:   120 + offset,
        posY:   100 + offset,
        width:  280,
        height: 240,
      },
    })
    notes.value = [created, ...notes.value]
    panelOpen.value = false
  } catch { /* ignore */ }
}

async function openNote(n: Note) {
  if (!n.opened) {
    n.opened = true
    await patchNote(n.id, { opened: true })
  }
  panelOpen.value = false
}

async function closeNote(n: Note) {
  n.opened = false
  await patchNote(n.id, { opened: false })
}

async function deleteNote(id: number) {
  if (!confirm('Hapus note ini? Tindakan ini tidak bisa di-undo.')) return
  try {
    await $fetch(`/api/personal-notes/${id}`, { method: 'DELETE' })
    notes.value = notes.value.filter(n => n.id !== id)
  } catch { /* ignore */ }
}

async function setColor(n: Note, color: string) {
  n.color = color
  await patchNote(n.id, { color })
}

// ── Share dialog ──────────────────────────────────────────────────────
const shareDialog = reactive<{
  open: boolean
  note: Note | null
  loading: boolean
  shares: ShareRow[]
  searchInput: string
  searchFocused: boolean
  searching: boolean
  searchResults: UserSearchRow[]
}>({
  open: false,
  note: null,
  loading: false,
  shares: [],
  searchInput: '',
  searchFocused: false,
  searching: false,
  searchResults: [],
})
async function openShareDialog(n: Note) {
  shareDialog.note = n
  shareDialog.shares = []
  shareDialog.searchInput = ''
  shareDialog.searchResults = []
  shareDialog.open = true
  await loadShares()
  // Pre-populate search dengan list user kosong-query untuk discovery cepat.
  await searchUsers('')
}
function closeShareDialog() {
  shareDialog.open = false
  shareDialog.note = null
}
async function loadShares() {
  if (!shareDialog.note) return
  shareDialog.loading = true
  try {
    const res = await $fetch<{ shares: ShareRow[] }>(`/api/personal-notes/${shareDialog.note.id}/shares`)
    shareDialog.shares = res.shares ?? []
  } catch (err: any) {
    console.error('[PersonalNotes] loadShares failed', err?.data ?? err)
    shareDialog.shares = []
  } finally {
    shareDialog.loading = false
  }
}
let searchTimer: any = null
watch(() => shareDialog.searchInput, () => {
  if (!shareDialog.open) return
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => searchUsers(shareDialog.searchInput), 250)
})
async function searchUsers(q: string) {
  shareDialog.searching = true
  try {
    const res = await $fetch<{ rows: UserSearchRow[] }>('/api/personal-notes/users-search', {
      query: { q },
    })
    // Filter out user yang sudah di-share — biar tidak bingung.
    const sharedIds = new Set(shareDialog.shares.map(s => s.userId))
    shareDialog.searchResults = (res.rows ?? []).filter(u => !sharedIds.has(u.id))
  } catch (err: any) {
    console.error('[PersonalNotes] searchUsers failed', err?.data ?? err)
    shareDialog.searchResults = []
  } finally {
    shareDialog.searching = false
  }
}
function onShareSearchBlur() {
  // Beri delay supaya klik di hasil sempat ke-handle.
  setTimeout(() => { shareDialog.searchFocused = false }, 150)
}
async function addShare(u: UserSearchRow, role: 'editor' | 'viewer') {
  if (!shareDialog.note) return
  try {
    const res = await $fetch<ShareRow>(`/api/personal-notes/${shareDialog.note.id}/shares`, {
      method: 'POST',
      body: { userId: u.id, role },
    })
    // Replace kalau sudah ada (upsert), atau append.
    const idx = shareDialog.shares.findIndex(s => s.userId === res.userId)
    if (idx >= 0) shareDialog.shares.splice(idx, 1, res)
    else shareDialog.shares.push(res)
    // Hapus dari hasil search list.
    shareDialog.searchResults = shareDialog.searchResults.filter(x => x.id !== u.id)
    shareDialog.searchInput = ''
  } catch (err: any) {
    console.error('[PersonalNotes] addShare failed', err?.data ?? err)
    alert(err?.data?.message ?? 'Gagal share note')
  }
}
async function updateShareRole(s: ShareRow) {
  if (!shareDialog.note) return
  try {
    await $fetch(`/api/personal-notes/${shareDialog.note.id}/shares/${s.userId}`, {
      method: 'PATCH',
      body: { role: s.role },
    })
  } catch (err: any) {
    console.error('[PersonalNotes] updateShareRole failed', err?.data ?? err)
    alert(err?.data?.message ?? 'Gagal ubah role')
  }
}
async function removeShare(s: ShareRow) {
  if (!shareDialog.note) return
  if (!confirm(`Hapus akses ${s.name} ke note ini?`)) return
  try {
    await $fetch(`/api/personal-notes/${shareDialog.note.id}/shares/${s.userId}`, {
      method: 'DELETE',
    })
    shareDialog.shares = shareDialog.shares.filter(x => x.userId !== s.userId)
  } catch (err: any) {
    console.error('[PersonalNotes] removeShare failed', err?.data ?? err)
    alert(err?.data?.message ?? 'Gagal hapus share')
  }
}

// Debounced patch — content + position + size + title. posX/posY/width/height
// di-floor karena pointer events report fractional pixel values (e.g., 1331.3125)
// sedangkan kolom DB INTEGER → bakal error tanpa rounding.
const patchTimers = new Map<number, any>()
function schedulePatch(n: Note) {
  if (patchTimers.has(n.id)) clearTimeout(patchTimers.get(n.id))
  const t = setTimeout(() => {
    patchNote(n.id, {
      title:   n.title,
      content: n.content,
      color:   n.color,
      posX:    Math.floor(n.posX),
      posY:    Math.floor(n.posY),
      width:   Math.floor(n.width),
      height:  Math.floor(n.height),
    })
    patchTimers.delete(n.id)
  }, 500)
  patchTimers.set(n.id, t)
}
async function patchNote(id: number, body: Partial<Note>) {
  try {
    await $fetch(`/api/personal-notes/${id}`, { method: 'PATCH', body })
  } catch (err: any) {
    // Log ke console biar saat develop terlihat kalau ada masalah — note tidak
    // tersimpan biasanya karena bug client-side payload atau auth expired.
    console.error('[PersonalNotes] patch failed', err?.data ?? err)
  }
}

// ── Drag + resize handlers ────────────────────────────────────────────
const dragState = reactive({
  mode: '' as '' | 'drag' | 'resize',
  noteId: 0,
  startX: 0, startY: 0,
  orig: { x: 0, y: 0, w: 0, h: 0 },
})
function onStickyPointerDown(e: PointerEvent, n: Note) {
  const t = e.target as HTMLElement | null
  if (!t) return
  if (t.closest(`[data-sticky-no-drag="${n.id}"]`)) return
  if (t.closest(`[data-sticky-resize="${n.id}"]`)) {
    dragState.mode    = 'resize'
    dragState.noteId  = n.id
    dragState.startX  = e.clientX
    dragState.startY  = e.clientY
    dragState.orig.w  = n.width
    dragState.orig.h  = n.height
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    e.preventDefault()
    return
  }
  if (t.closest(`[data-sticky-drag="${n.id}"]`)) {
    dragState.mode   = 'drag'
    dragState.noteId = n.id
    dragState.startX = e.clientX
    dragState.startY = e.clientY
    dragState.orig.x = n.posX
    dragState.orig.y = n.posY
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
}
function onStickyPointerMove(e: PointerEvent, n: Note) {
  if (!dragState.mode || dragState.noteId !== n.id) return
  if (dragState.mode === 'drag') {
    const nextX = dragState.orig.x + (e.clientX - dragState.startX)
    const nextY = dragState.orig.y + (e.clientY - dragState.startY)
    if (typeof window !== 'undefined') {
      n.posX = Math.min(window.innerWidth  - 80, Math.max(-(n.width - 80), nextX))
      n.posY = Math.min(window.innerHeight - 40, Math.max(0,               nextY))
    } else {
      n.posX = nextX; n.posY = nextY
    }
  } else if (dragState.mode === 'resize') {
    const nextW = dragState.orig.w + (e.clientX - dragState.startX)
    const nextH = dragState.orig.h + (e.clientY - dragState.startY)
    const maxW = typeof window !== 'undefined' ? window.innerWidth  - n.posX - 8 : nextW
    const maxH = typeof window !== 'undefined' ? window.innerHeight - n.posY - 8 : nextH
    n.width  = Math.max(180, Math.min(maxW, nextW))
    n.height = Math.max(140, Math.min(maxH, nextH))
  }
}
function onStickyPointerUp(e: PointerEvent) {
  if (!dragState.mode) return
  const n = notes.value.find(x => x.id === dragState.noteId)
  if (n) schedulePatch(n)
  dragState.mode = ''
  dragState.noteId = 0
  try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* ignore */ }
}

// ── Visual helpers ────────────────────────────────────────────────────
// Sticky note styling — pastel + subtle gradient untuk efek "kertas".
function colorBg(c: string): string {
  const map: Record<string, string> = {
    yellow: 'bg-gradient-to-br from-yellow-50 via-yellow-100 to-yellow-200/80',
    pink:   'bg-gradient-to-br from-pink-50   via-pink-100   to-pink-200/80',
    blue:   'bg-gradient-to-br from-sky-50    via-sky-100    to-sky-200/80',
    green:  'bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-200/80',
    purple: 'bg-gradient-to-br from-violet-50 via-violet-100 to-violet-200/80',
    orange: 'bg-gradient-to-br from-orange-50 via-orange-100 to-orange-200/80',
  }
  return map[c] || map.yellow
}
function colorHeader(c: string): string {
  // Tint translucent dari warna note, gives "header glass overlay" feel.
  const map: Record<string, string> = {
    yellow: 'bg-yellow-200/60',
    pink:   'bg-pink-200/60',
    blue:   'bg-sky-200/60',
    green:  'bg-emerald-200/60',
    purple: 'bg-violet-200/60',
    orange: 'bg-orange-200/60',
  }
  return map[c] || map.yellow
}
function colorFoldedCorner(c: string): string {
  // Sedikit lebih gelap dari base — efek "kertas terlipat" di pojok bawah.
  const map: Record<string, string> = {
    yellow: 'bg-yellow-300/60',
    pink:   'bg-pink-300/60',
    blue:   'bg-sky-300/60',
    green:  'bg-emerald-300/60',
    purple: 'bg-violet-300/60',
    orange: 'bg-orange-300/60',
  }
  return map[c] || map.yellow
}
function colorDot(c: string): string {
  return COLORS.find(x => x.key === c)?.dot || 'bg-yellow-400'
}
function formatDt(ts: string): string {
  if (!ts) return ''
  const d = new Date(ts)
  return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}
</script>
